<template lang="pug">
web-app-layout(:showLogo="true" :noPadding="true")
  .settings-page
    .settings-bar
      button.back-button(type="button" @click="goBack")
        img.back-icon(src="@/assets/icons/chevron-right.svg" alt="Back")
        span Mailing address

    .settings-container
      p.intro
        template(v-if="fromWalletSetup")
          | Add {{ ownerLabel }} mailing address for tax receipts. This finishes the Giving Wallet setup.
        template(v-else) Used on {{ ownerLabel }} donation tax receipts.

      p.form-error(v-if="loadError")
        | {{ loadError }}
        el-button.ms-2(link type="primary" @click="load") Retry

      .settings-card(v-else v-loading="isLoading")
        el-form(label-position="top" @submit.prevent="save")
          el-form-item(label="Street address" :error="problems.line1")
            el-input(v-model="form.line1" maxlength="100" autocomplete="address-line1")
          el-form-item(label="Apartment, suite, etc. (optional)")
            el-input(v-model="form.line2" maxlength="100" autocomplete="address-line2")
          el-form-item(label="City" :error="problems.city")
            el-input(v-model="form.city" maxlength="60" autocomplete="address-level2")
          .row
            .col-6
              el-form-item(label="State" :error="problems.state")
                el-input(
                  v-model="form.state"
                  maxlength="2"
                  placeholder="CA"
                  autocomplete="address-level1"
                )
            .col-6
              el-form-item(label="ZIP code" :error="problems.zip")
                el-input(
                  v-model="form.zip"
                  maxlength="5"
                  inputmode="numeric"
                  placeholder="92101"
                  autocomplete="postal-code"
                )
          p.field-note US addresses only.

          p.form-error(v-if="saveError") {{ saveError }}

          el-button.primary-btn(
            native-type="submit"
            :loading="isSaving"
            :disabled="isLoading || (!fromWalletSetup && !isChanged)"
          ) Save address
          el-button.skip-btn(
            v-if="fromWalletSetup"
            text
            :disabled="isSaving"
            @click="continueToWallet"
          ) Skip for now
</template>

<script lang="ts">
import {
  computed, defineComponent, onMounted, reactive, ref, watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useActingEntity } from '@/composables/useActingEntity';
import { useMailingAddress } from '@/composables/useMailingAddress';
import {
  EMPTY_ADDRESS, addressProblems, isSameAddress, type AddressProblems, type MailingAddress,
} from '@/utils/mailingAddress';
import webAppToast from '@/utils/webAppToast';
import WebAppLayout from '../dashboard/WebAppLayout.vue';

/**
 * Mailing address of the acting entity (or of `?orgId`, set by the wallet setup), for tax receipts.
 * `?from=setup` is the last step of creating a Giving Wallet: it can be skipped and moves on to the
 * wallet after saving.
 */
export default defineComponent({
  name: 'MailingAddress',
  components: { WebAppLayout },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const { organization: actingOrganization, orgId: actingOrgId } = useActingEntity();
    const { load: loadAddress, save: saveAddress } = useMailingAddress();

    const orgId = computed(() => (route.query.orgId as string) || actingOrgId.value || null);
    const fromWalletSetup = computed(() => route.query.from === 'setup');
    const ownerLabel = computed(() => {
      if (!orgId.value) return 'your';
      return actingOrganization.value?.id === orgId.value
        ? `${actingOrganization.value.name}'s`
        : 'the organization\'s';
    });

    const form = reactive<MailingAddress>({ ...EMPTY_ADDRESS });
    const saved = ref<MailingAddress>({ ...EMPTY_ADDRESS });
    const problems = ref<AddressProblems>({});
    const isLoading = ref(false);
    const isSaving = ref(false);
    const loadError = ref('');
    const saveError = ref('');
    const isChanged = computed(() => !isSameAddress(form, saved.value));

    const load = async () => {
      isLoading.value = true;
      loadError.value = '';
      saveError.value = '';
      problems.value = {};
      try {
        const result = await loadAddress(orgId.value);
        if (result.error || !result.address) {
          loadError.value = result.error || 'Couldn\'t load the mailing address.';
          return;
        }
        saved.value = result.address;
        Object.assign(form, result.address);
      } finally {
        isLoading.value = false;
      }
    };

    const continueToWallet = () => {
      if (orgId.value && orgId.value !== actingOrgId.value) {
        // Wallet set up for an organization the user isn't acting as: its causes page shows it
        router.push({ path: '/web/supported-causes', query: { orgId: orgId.value } });
      } else {
        router.push('/web/wallet/dashboard');
      }
    };

    const save = async () => {
      if (isSaving.value || isLoading.value) return;
      saveError.value = '';
      problems.value = addressProblems(form);
      if (Object.keys(problems.value).length) return;

      isSaving.value = true;
      try {
        const result = await saveAddress(form, orgId.value);
        if (result.error || !result.address) {
          saveError.value = result.error || 'Couldn\'t save the mailing address. Please try again.';
          return;
        }
        saved.value = result.address;
        Object.assign(form, result.address);
        webAppToast.success('Mailing address saved.');
        if (fromWalletSetup.value) continueToWallet();
      } finally {
        isSaving.value = false;
      }
    };

    const goBack = () => {
      if (fromWalletSetup.value) continueToWallet();
      else if (window.history.state?.back) router.back();
      else router.push('/web/wallet/dashboard');
    };

    onMounted(load);
    // Switching account in the menu: show the new entity's address (drops a setup ?orgId)
    watch(actingOrgId, (id, previous) => {
      if (id === previous) return;
      if (route.query.orgId) {
        const query = { ...route.query };
        delete query.orgId;
        delete query.from;
        router.replace({ query }).then(load);
      } else {
        load();
      }
    });

    return {
      form,
      problems,
      isLoading,
      isSaving,
      loadError,
      saveError,
      isChanged,
      fromWalletSetup,
      ownerLabel,
      load,
      save,
      goBack,
      continueToWallet,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '../settings/settings';

.intro {
  margin: 0 0 16px;
  font-size: 14px;
  color: #6b7280;
}

.skip-btn {
  display: block;
  width: 100%;
  margin: 8px 0 0;
  color: #374151;
}
</style>
